import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { API } from "@/App";
import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import { Vine, FloatLeaf } from "@/components/Botanical";
import PhotoMarquee from "@/components/PhotoMarquee";
import { BOOKING_URL } from "@/components/Nav";

function Price({ value, fromCls }) {
  const m = (value || "").match(/^(from\s+)(.+)$/i);
  if (!m) return <>{value}</>;
  return (
    <>
      <span className={fromCls}>{m[1]}</span>
      {m[2]}
    </>
  );
}

export default function Services() {
  const [data, setData] = useState(null);
  const [seats, setSeats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => setData(r.data))
      .catch(() => {});
    axios
      .get(`${API}/memberships`)
      .then((r) => setSeats(r.data))
      .catch(() => {});
  }, []);

  const PLAN_FALLBACK = {
    essentials: {
      name: "The Essentials Edit",
      original_price: "\u20b95,000",
      price: "\u20b92,000",
      blurb: "Member rates across all waxing & threading services — from Mediterranean waxing to the threading studio.",
      features: [
        "Member pricing on every waxing & threading visit",
        "Covers roll-on strip, Mediterranean & threading studios",
        "Personal membership for one guest",
      ],
    },
    family: {
      name: "The Family Circle",
      original_price: "\u20b925,000",
      price: "\u20b910,000",
      blurb: "One membership for the whole family — husband, wife and two kids — with member rates on every service on the menu.",
      features: [
        "Member pricing on all services, all twelve chapters",
        "Four named members — husband, wife & two kids",
        "From cuts and colour to rituals, skin, nails & makeup",
      ],
    },
  };
  const plans = seats?.plans || PLAN_FALLBACK;

  return (
    <div data-testid="services-page">
      <section className="relative min-h-[55vh] flex items-end bg-[#F9FAF8] overflow-hidden pt-36 pb-14">
        <Vine className="absolute left-6 top-24 h-[55%] w-auto opacity-30" />
        <FloatLeaf className="w-24 right-[14%] top-[20%] opacity-25" delay={0.7} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full relative z-10">
          <MaskedLine onLoad className="mb-6">
            <Eyebrow>Services &amp; price menu</Eyebrow>
          </MaskedLine>
          <h1 className="font-display font-light leading-[0.95] tracking-tight text-[#0A0A0A]">
            <MaskedLine onLoad delay={0.15} className="text-[13vw] md:text-8xl">
              <span>The</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.3} className="text-[13vw] md:text-8xl">
              <span className="italic text-[#11261A]">Menu of Rituals</span>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 text-base md:text-lg text-[#4A4A4A] max-w-xl leading-relaxed">
              Every service begins with a conversation. Prices are starting points — your final
              quote follows a relaxed one-to-one consultation.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20" data-testid="services-menu-section">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          {!data ? (
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#4A4A4A]" data-testid="services-loading">
              Preparing the menu…
            </p>
          ) : (
            <div className="space-y-16" data-testid="services-menu">
              {data.categories.map((cat, ci) => (
                <FadeUp key={cat.category}>
                  <div className="flex items-baseline gap-5 mb-3">
                    <span className="font-display font-light text-3xl text-[#11261A]/20">
                      {String(ci + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight text-[#0A0A0A]">
                      {cat.category}
                    </h2>
                    <span className="leader" />
                  </div>
                  {cat.blurb && (
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-8">{cat.blurb}</p>
                  )}
                  {cat.sections.map((sec) => (
                    <div key={sec.title} className="mb-10 last:mb-0">
                      <div className="flex items-baseline justify-between border-b border-[#E5E7E3] pb-2.5 mb-5">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[#1B3B28] font-semibold">{sec.title}</p>
                      </div>
                      <ul className="space-y-4">
                        {sec.items.map((item) => (
                          <li
                            key={item.name}
                            className="group"
                            data-testid={`service-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            <div className="flex items-baseline">
                              <span className="text-base md:text-lg text-[#0A0A0A] transition-colors duration-300 group-hover:text-[#1B3B28]">
                                {item.name}
                              </span>
                              <span className="leader" />
                              <span className="text-right whitespace-nowrap">
                                {item.standard ? (
                                  <>
                                    <span className="font-display text-2xl md:text-3xl text-[#B9975B] leading-none">
                                      <Price value={item.member || item.standard} fromCls="font-sans italic text-sm md:text-base tracking-normal" />
                                    </span>
                                    {item.member && (
                                      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#B9975B]/80 mt-1">
                                        member price
                                      </span>
                                    )}
                                    <span className="block text-xs text-[#4A4A4A]/70 mt-1.5">Standard <Price value={item.standard} fromCls="text-[10px] italic" /></span>
                                  </>
                                ) : (
                                  <span className="font-display italic text-base text-[#11261A]">On consultation</span>
                                )}
                              </span>
                            </div>
                            {item.note && (
                              <p className="text-xs uppercase tracking-[0.2em] text-[#8EA696] mt-1">{item.note}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </FadeUp>
              ))}
              <FadeUp>
                <p className="text-sm text-[#4A4A4A] leading-relaxed border-t border-[#E5E7E3] pt-8" data-testid="services-note">
                  {data.note}
                </p>
              </FadeUp>
            </div>
          )}
        </div>
      </section>
      <PhotoMarquee />

      <section className="relative bg-[#F9FAF8] py-16 md:py-24 overflow-hidden border-t border-[#E5E7E3]" data-testid="membership-section">
        <FloatLeaf className="w-28 right-[6%] top-[12%] opacity-20" delay={0.5} />
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
          <FadeUp>
            <Eyebrow>The Orgænics Circle — memberships</Eyebrow>
          </FadeUp>
          <MaskedLine delay={0.1}>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-[#0A0A0A] mt-4 max-w-2xl">
              Member rates, <span className="italic text-[#11261A]">every visit.</span>
            </h2>
          </MaskedLine>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] max-w-xl leading-relaxed">
              Every price on our menu carries a member rate. Two founding plans, open to the first
              fifty enrolments only.
            </p>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {["essentials", "family"].map((id, i) => {
              const plan = plans[id];
              const dark = id === "family";
              const claimed = seats?.claimed?.[id] ?? 0;
              const total = seats?.total_seats ?? 50;
              return (
                <FadeUp key={id} delay={0.1 + i * 0.1}>
                  <div
                    className={
                      dark
                        ? "h-full bg-[#11261A] text-[#F9FAF8] p-10 md:p-12 flex flex-col relative overflow-hidden grain transition-all duration-500 hover:shadow-[0_20px_50px_rgba(17,38,26,0.25)]"
                        : "relative h-full border border-[#E5E7E3] bg-white p-10 md:p-12 flex flex-col transition-all duration-500 hover:border-[#11261A] hover:shadow-[0_20px_50px_rgba(17,38,26,0.08)]"
                    }
                    data-testid={`membership-card-${id}`}
                  >
                    <p className={`text-[11px] uppercase tracking-[0.3em] text-[#8EA696] ${dark ? "relative z-10" : ""}`}>{plan.name}</p>
                    <p className={`mt-5 text-lg line-through decoration-[#8EA696] ${dark ? "text-[#F9FAF8]/45 relative z-10" : "text-[#4A4A4A]/60"}`} data-testid={`membership-original-price-${id}`}>{plan.original_price}</p>
                    <p className={`font-display font-light text-5xl md:text-6xl mt-1 ${dark ? "relative z-10" : "text-[#11261A]"}`}>{plan.price}</p>
                    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-[#F9FAF8]/75 relative z-10" : "text-[#4A4A4A]"}`}>{plan.blurb}</p>
                    <ul className={`mt-8 space-y-3 text-sm flex-1 ${dark ? "text-[#F9FAF8]/75 relative z-10" : "text-[#4A4A4A]"}`}>
                      {(plan.features || []).map((f, fi) => (
                        <li key={fi} className="flex gap-3">
                          <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${dark ? "bg-[#8EA696]" : "bg-[#1B3B28]"}`} />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {seats && (
                      <div className={`mt-8 ${dark ? "relative z-10" : ""}`} data-testid={`membership-counter-${id}`}>
                        <div className={`flex justify-between text-[10px] uppercase tracking-[0.25em] ${dark ? "text-[#F9FAF8]/70" : "text-[#4A4A4A]"}`}>
                          <span>{claimed} of {total} claimed</span>
                          <span className={`font-semibold ${dark ? "text-[#8EA696]" : "text-[#1B3B28]"}`}>{total - claimed} left</span>
                        </div>
                        <div className={`mt-2 h-[3px] overflow-hidden ${dark ? "bg-[#1B3B28]" : "bg-[#E5E7E3]"}`}>
                          <motion.div
                            className={`h-full ${dark ? "bg-[#8EA696]" : "bg-[#1B3B28]"}`}
                            initial={{ width: 0 }}
                            whileInView={{ width: `${(claimed / total) * 100}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                          />
                        </div>
                      </div>
                    )}
                    <p className={`mt-6 text-[10px] uppercase tracking-[0.3em] font-semibold ${dark ? "text-[#8EA696] relative z-10" : "text-[#1B3B28]"}`}>First {total} enrolments only</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp delay={0.3}>
            <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
              <p className="text-xs text-[#4A4A4A] leading-relaxed max-w-lg">
                Fees exclusive of GST · full terms in the Membership Agreement · benefits have no
                cash value · valid for the period recorded at enrolment · memberships are valid
                only at Salon Orgænics, VJ Yashwin Orrizonte, near Panchshil Towers, Kharadi,
                Pune 411014.
              </p>
              <a
                href="https://wa.me/919158000424?text=Hello%20Salon%20Org%C3%A6nics%2C%20I%27d%20like%20to%20enrol%20in%20a%20membership."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="membership-enrol-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:bg-black hover:shadow-xl hover:-translate-y-0.5"
              >
                Enrol via WhatsApp
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="relative bg-[#11261A] text-[#F9FAF8] grain py-16 md:py-24 overflow-hidden">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-left relative z-10">
          <MaskedLine>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight max-w-2xl">
              Ready when <span className="italic text-[#8EA696]">you are.</span>
            </h2>
          </MaskedLine>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-[#F9FAF8]/70 max-w-lg leading-relaxed">
              Open every day, 11:00 AM to 10:00 PM. Reserve your chair online — it takes less than
              a minute.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-book-now-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#F9FAF8] text-[#11261A] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:bg-white hover:shadow-xl hover:-translate-y-0.5"
            >
              Book your visit
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
